import React, { useState, useEffect, useRef } from 'react';
import './ConceptStorePage.css';
import Header from './Header';
import Footer from './Footer';
import { 
  FaEnvelope, 
  FaWhatsapp, 
  FaUsers,
  FaStore,
  FaChartLine,
  FaUsersCog
} from 'react-icons/fa';

function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { threshold: 0.3 }
    );
    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [ref]);
  return isIntersecting;
}

function CounterConceptStore({ to }) {
  const [count, setCount] = useState(1);
  const ref = useRef();
  const visible = useOnScreen(ref);
  useEffect(() => {
    if (!visible) return;
    let start = 1;
    let end = to;
    let duration = 1200;
    let startTime = null;
    function animate(ts) {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setCount(Math.floor(progress * (end - start) + start));
      if (progress < 1) requestAnimationFrame(animate);
      else setCount(end);
    }
    requestAnimationFrame(animate);
  }, [visible, to]);
  return <span ref={ref} className="contador-concept-store">+{count.toLocaleString()}</span>;
}

const ConceptStorePage = () => {
  const [fraseIndex, setFraseIndex] = useState(0);
  const [fraseAnim, setFraseAnim] = useState('slide-in');
  const fraseTimeout = useRef();

  // Frases de marketing persuasivas
  const frasesMarketing = [
    'ESPACIO PARA EMPRENDEDORES',
    'VISIBILIDAD Y CRECIMIENTO'
  ];

  // Efecto para rotar las frases
  useEffect(() => {
    setFraseAnim('slide-in');
    fraseTimeout.current = setTimeout(() => {
      setFraseAnim('slide-out');
      setTimeout(() => {
        setFraseIndex(i => (i + 1) % frasesMarketing.length);
        setFraseAnim('slide-in');
      }, 500);
    }, 3000);
    return () => clearTimeout(fraseTimeout.current);
  }, [fraseIndex, frasesMarketing.length]);

  const handleContact = (type) => {
    switch(type) {
      case 'whatsapp':
        window.open('http://wa.me/524433904159', '_blank');
        break;
      case 'email':
        window.open('mailto:recomiendateconceptstore@gmail.com', '_blank');
        break;
      case 'instagram':
        window.open('https://www.instagram.com/recomiendateconceptstore/', '_blank');
        break;
      case 'facebook':
        window.open('https://www.facebook.com/RecomiendaT/', '_blank');
        break;
      case 'website':
        window.open('https://recomiendate.com', '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Header />
      <main className="concept-store-page">
        <div className="concept-store-hero">
          <div className="container">
            <h1 className="concept-store-titulo">
              <span className="titulo-linea-1">Recomienda-T</span>
              <span className="titulo-linea-2">Concept Store</span>
            </h1>
            <p 
              className={`concept-store-subtitulo concept-store-slider-frase ${fraseAnim}`}
            >
              {frasesMarketing[fraseIndex]}
            </p>
            <p className="concept-store-subtitulo">
              <span className="destacado">¡Impulsa tu negocio con nosotros!</span>
            </p>
          </div>
        </div>

        <div className="concept-store-content">
          <div className="container">
            {/* Imagen del Concept Store */}
            <div className="concept-store-image-section">
              <div className="concept-store-image-container">
                <img 
                  src="/images/concept_store/concept-store-image.jpg" 
                  alt="Recomienda-T Concept Store" 
                  className="concept-store-image"
                />
              </div>
            </div>

            {/* Descripción principal */}
            <div className="description-section">
              <div className="description-card">
                <h2 className="description-title">¿Qué es Recomienda-T Concept Store?</h2>
                <div className="description-content">
                  <p className="description-text">
                    <span className="highlight">Recomienda-T Concept Store</span> es mucho más que una tienda; es el <span className="highlight">hub empresarial</span> donde los emprendedores y marcas locales encuentran su <span className="highlight">plataforma de lanzamiento al éxito</span>.
                  </p>
                  <p className="description-text">
                    Ofrecemos <span className="highlight">exhibición premium</span>, <span className="highlight">venta estratégica</span> y <span className="highlight">promoción digital</span> de productos únicos, brindando a tu negocio la <span className="highlight">visibilidad que merece</span> y las <span className="highlight">conexiones que necesitas</span> para crecer exponencialmente.
                  </p>
                  <p className="description-text">
                    Nuestro objetivo es crear una <span className="highlight">comunidad emprendedora de alto impacto</span>, proporcionando las <span className="highlight">herramientas, recursos y oportunidades</span> que transforman ideas en negocios exitosos.
                  </p>
                </div>
                <div className="description-cta">
                  <span className="cta-text">¡Únete a la revolución empresarial!</span>
                </div>
              </div>
            </div>

            {/* Servicios */}
            <div className="concept-servicios-section">
              <h2 className="concept-servicios-titulo">
                <CounterConceptStore to={4} /> SERVICIOS DE LA CONCEPT STORE
              </h2>
              <div className="concept-servicios-grid">
                <div className="concept-servicio-card">
                  <div className="concept-servicio-icono">
                    <FaStore />
                  </div>
                  <h3 className="concept-servicio-nombre">Espacio de Exhibición y Venta</h3>
                  <p className="concept-servicio-desc">Amplio espacio físico para exhibir y vender tus productos de manera profesional</p>
                </div>
                <div className="concept-servicio-card">
                  <div className="concept-servicio-icono">
                    <FaChartLine />
                  </div>
                  <h3 className="concept-servicio-nombre">Registro de Ventas Diarias</h3>
                  <p className="concept-servicio-desc">Seguimiento detallado de tus ventas con reportes diarios para optimizar tu negocio</p>
                </div>
                <div className="concept-servicio-card">
                  <div className="concept-servicio-icono">
                    <FaUsers />
                  </div>
                  <h3 className="concept-servicio-nombre">Difusión en Redes Sociales</h3>
                  <p className="concept-servicio-desc">Promoción en nuestras redes sociales oficiales para aumentar tu visibilidad</p>
                </div>
                <div className="concept-servicio-card">
                  <div className="concept-servicio-icono">
                    <FaUsersCog />
                  </div>
                  <h3 className="concept-servicio-nombre">Talleres y Capacitación</h3>
                  <p className="concept-servicio-desc">Programas de formación y desarrollo para potenciar tus habilidades empresariales</p>
                </div>
              </div>
            </div>

            {/* Mapa interactivo */}
            <div className="mapa-section">
              <h2 className="mapa-titulo">Ubicación de Nuestra Tienda</h2>
              <div className="mapa-container">
                <iframe
                  src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Calle+Vicente+Santamaría+1516+Colonia+Félix+Ireta+Plaza+Patio+Río+Local+6+Morelia+Michoacán+Mexico"
                  width="100%"
                  height="450"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación Recomienda-T Concept Store"
                ></iframe>
                <div className="mapa-info">
                  <div className="mapa-direccion">
                    <h3>Recomienda-T Concept Store</h3>
                    <p>Calle Vicente Santamaría #1516<br />Colonia Félix Ireta, Plaza Patio Río<br />Local 6, Morelia, Michoacán</p>
                  </div>
                  <div className="mapa-contacto">
                    <div className="contacto-rapido">
                      <button 
                        className="contacto-rapido-btn whatsapp"
                        onClick={() => handleContact('whatsapp')}
                      >
                        <FaWhatsapp />
                        <span>WhatsApp</span>
                      </button>
                      <button 
                        className="contacto-rapido-btn email"
                        onClick={() => handleContact('email')}
                      >
                        <FaEnvelope />
                        <span>Email</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ConceptStorePage; 